import { isCancelledError } from "@-/com.npmjs.tanstack__react-query";

// A cancelled read is a SUPERSEDED read, never a failed one — so it must not abort a navigation.
//
// Routing reads go through the shared query cache, so the read is a cache entry like any other
// and TanStack can cancel it mid-flight (an observer of a key this read composes going away
// while the fetch is in flight, a `cancelQueries` from the sync/settings paths, a cache reset).
// The awaiting caller is then rejected with `CancelledError` — and note that this particular
// rejection is INVISIBLE: the cancel paths that reach a `fetchQuery` awaiter deliberately skip
// the query cache's `onError`, so nothing is logged as a query failure.
//
// That is harmless for a rendered query (its observer just refetches) but catastrophic here: the
// rating transaction has ALREADY committed by the time this runs, so letting the rejection
// through leaves the user parked on a card they just rated. Retrying is both safe and correct —
// re-reading is exactly what a cancellation is asking us to do, and the write-clock guard still
// proves the value we settle on is current with the rating that landed.
const NEXT_ROUTE_READ_ATTEMPTS = 3;

export async function readThroughCancellation<TData>(read: () => Promise<TData>): Promise<TData> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await read();
    } catch (error) {
      // Bounded: a read that keeps losing to cancellation has a cause worth surfacing (a
      // connection swap, a sign-out) rather than spinning here forever.
      if (!isCancelledError(error) || attempt >= NEXT_ROUTE_READ_ATTEMPTS) {
        throw error;
      }
    }
  }
}
